import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MessageCircle, Mail, Link2, FileText, Trash2, Receipt } from "lucide-react";
import { formatDateDE, toYMDLocal } from "@/lib/utils";
import { CustomerPicker } from "@/components/CustomerPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const num = (v, d = 2) => Number(v || 0).toFixed(d).replace(".", ",");

const EMPTY = {
  betrieb: "", kontakt_name: "", phone: "", email: "",
  datum: toYMDLocal(),
  von: "", bis: "", pause_min: 0, stundensatz_eur: "", notiz: "",
};

const calcHours = (von, bis, pause) => {
  const m = (t) => {
    const parts = String(t || "").split(":");
    if (parts.length !== 2) return null;
    const h = Number(parts[0]), mi = Number(parts[1]);
    if (Number.isNaN(h) || Number.isNaN(mi)) return null;
    return h * 60 + mi;
  };
  const s = m(von), e0 = m(bis);
  if (s === null || e0 === null) return 0;
  if (e0 === s) return 0;
  let e = e0;
  if (e < s) e += 24 * 60;
  return Math.max(0, Math.round(((e - s - (Number(pause) || 0)) / 60) * 100) / 100);
};

const statusBadge = (r) => {
  const base = "text-[9px] uppercase tracking-[0.15em] px-2 py-1 border";
  if (r.invoiced_at) return <span className={`${base} border-[#BA6D52] text-[#BA6D52]`}>abgerechnet</span>;
  if (r.confirmed_at) return <span className={`${base} border-emerald-300 text-emerald-700 bg-emerald-50`}>bestätigt</span>;
  if (r.sent_at) return <span className={`${base} border-amber-300 text-amber-700 bg-amber-50`}>gesendet</span>;
  return <span className={`${base} border-[#E3DECE] text-[#757067]`}>offen</span>;
};

export default function ShiftsTab({ pwd, onCreateInvoice, prefillCustomer, onPrefillUsed }) {
  const [form, setForm] = useState(EMPTY);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const hours = calcHours(form.von, form.bis, form.pause_min);
  const total = hours * (Number(form.stundensatz_eur) || 0);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stundennachweise`, { headers: { "X-Admin-Password": pwd } });
      setList(data || []);
    } catch (err) {
      toast.error("Stundennachweise konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!prefillCustomer) return;
    setForm((f) => ({
      ...f,
      betrieb: prefillCustomer.betrieb || f.betrieb,
      kontakt_name: prefillCustomer.ansprechpartner || f.kontakt_name,
      phone: prefillCustomer.telefon || f.phone,
      email: prefillCustomer.email || f.email,
      stundensatz_eur: prefillCustomer.stundensatz_eur > 0 ? prefillCustomer.stundensatz_eur : f.stundensatz_eur,
    }));
    onPrefillUsed?.();
  }, [prefillCustomer, onPrefillUsed]);

  const save = async () => {
    if (!form.betrieb.trim() || !form.von || !form.bis) {
      toast.error("Betrieb sowie von/bis ausfüllen");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/stundennachweise`, {
        ...form,
        pause_min: Number(form.pause_min) || 0,
        stundensatz_eur: Number(form.stundensatz_eur) || 0,
      }, { headers: { "X-Admin-Password": pwd } });
      toast.success(`Nachweis ${data.nachweis_number} angelegt — jetzt per WhatsApp senden`);
      setForm({ ...EMPTY, datum: form.datum, stundensatz_eur: form.stundensatz_eur });
      await load();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const markSent = async (r, via) => {
    try {
      await axios.post(`${API}/admin/stundennachweise/${r.id}/sent?via=${via}`, {}, { headers: { "X-Admin-Password": pwd } });
      load();
    } catch { /* still fine */ }
  };

  const sendWhatsApp = (r) => {
    window.open(r.whatsapp_url, "_blank", "noopener");
    markSent(r, "whatsapp");
  };

  const sendEmail = async (r) => {
    const to = r.email || window.prompt("E-Mail-Adresse des Betriebs:");
    if (!to) return;
    try {
      await axios.post(`${API}/admin/stundennachweise/${r.id}/send-email`, { email: to },
        { headers: { "X-Admin-Password": pwd } });
      toast.success("Bestätigungs-Anfrage per E-Mail verschickt");
      load();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    }
  };

  const copyLink = async (r) => {
    try {
      await navigator.clipboard.writeText(r.confirm_url);
      toast.success("Link kopiert");
    } catch {
      window.prompt("Link kopieren:", r.confirm_url);
    }
  };

  const openPdf = async (r) => {
    try {
      const res = await axios.get(`${API}/admin/stundennachweise/${r.id}/pdf`, {
        headers: { "X-Admin-Password": pwd }, responseType: "blob",
      });
      window.open(URL.createObjectURL(res.data), "_blank", "noopener");
    } catch (err) {
      toast.error("PDF konnte nicht geladen werden");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Nachweis ${r.nachweis_number} (${r.betrieb}) wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/stundennachweise/${r.id}`, { headers: { "X-Admin-Password": pwd } });
      toast.success("Nachweis gelöscht");
      load();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    }
  };

  const toInvoice = (r) => {
    onCreateInvoice?.({
      form: {
        betrieb: r.betrieb,
        ansprechpartner: r.kontakt_name || "",
        email: r.email || "",
        telefon: r.phone || "",
        ort: "",
        einsatzdatum: r.datum,
        notes: "",
        admin_notes: `Stundennachweis ${r.nachweis_number} — bestätigt von ${r.confirmed_by} am ${r.confirmed_at ? new Date(r.confirmed_at).toLocaleDateString("de-DE") : ""}`,
      },
      lineItems: [{
        type_key: "mietkoch_werktag",
        name: `Mietkoch-Stunden am ${formatDateDE(r.datum)}`,
        description: `Arbeitszeit ${r.von}–${r.bis} Uhr${r.pause_min ? ` (Pause ${r.pause_min} Min.)` : ""} · bestätigt durch ${r.confirmed_by}`,
        quantity: r.hours,
        unit_name: "Stunde",
        unit_price_net: r.stundensatz_eur || 0,
        tax_rate: 19,
      }],
      shiftId: r.id,
    });
    toast.info("Leistungsdatum und Bezeichnung kannst du in der Rechnung frei anpassen");
  };

  return (
    <div data-testid="shifts-tab" className="space-y-10">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Stundennachweis</p>
        <p className="text-sm text-[#757067] mt-1 max-w-2xl">
          Für spontane Einsätze: Stunden erfassen, dem Küchenchef per WhatsApp einen Bestätigungslink
          schicken — er unterschreibt mit dem Finger, du bekommst den Nachweis als PDF per Mail.
          Die Rechnung erstellst du später (auch mit abweichendem Leistungsdatum).
        </p>
      </div>

      <div className="border border-[#E3DECE] bg-[#FAFAF7] p-5 md:p-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-4">Neuer Nachweis</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Betrieb *</label>
            <div className="mt-1">
              <CustomerPicker
                pwd={pwd}
                value={form.betrieb}
                onChange={(v) => set("betrieb", v)}
                onSelect={(k) => setForm((f) => ({
                  ...f,
                  betrieb: k.betrieb,
                  kontakt_name: k.ansprechpartner || f.kontakt_name,
                  phone: k.telefon || f.phone,
                  email: k.email || f.email,
                  stundensatz_eur: k.stundensatz_eur > 0 ? k.stundensatz_eur : f.stundensatz_eur,
                }))}
                placeholder="z. B. Hotel Zentlinde GbR"
                testId="shift-betrieb"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Küchenchef / Ansprechpartner</label>
            <input type="text" value={form.kontakt_name} onChange={(e) => set("kontakt_name", e.target.value)}
              data-testid="shift-kontakt" placeholder="Name"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Handynummer (WhatsApp)</label>
            <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
              data-testid="shift-phone" placeholder="0151 2345678"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">E-Mail (optional)</label>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
              data-testid="shift-email" placeholder="kueche@betrieb.de"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Einsatztag *</label>
            <input type="date" value={form.datum} onChange={(e) => set("datum", e.target.value)}
              data-testid="shift-datum"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-sm" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Von *</label>
              <input type="time" value={form.von} onChange={(e) => set("von", e.target.value)}
                data-testid="shift-von"
                className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-2 py-3 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Bis *</label>
              <input type="time" value={form.bis} onChange={(e) => set("bis", e.target.value)}
                data-testid="shift-bis"
                className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-2 py-3 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Pause Min.</label>
              <input type="number" min="0" step="5" value={form.pause_min}
                onChange={(e) => set("pause_min", e.target.value)} data-testid="shift-pause"
                className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-2 py-3 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Stundensatz netto €</label>
            <input type="number" min="0" step="0.5" value={form.stundensatz_eur}
              onChange={(e) => set("stundensatz_eur", e.target.value)} data-testid="shift-rate" placeholder="45"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Notiz (optional)</label>
            <input type="text" value={form.notiz} onChange={(e) => set("notiz", e.target.value)}
              data-testid="shift-notiz" placeholder="z. B. Posten Garde / Service à la carte"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-3 text-sm" />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-4 bg-white border border-[#E3DECE] px-4 py-3">
          <div className="text-sm">
            <span className="text-[#757067]">Arbeitsstunden: </span>
            <b className="text-[#BA6D52]" data-testid="shift-hours-preview">{num(hours)} Std.</b>
            {Number(form.stundensatz_eur) > 0 && (
              <span className="text-[#757067]"> · Summe netto <b className="text-[#2C2A26]">{num(total)} €</b></span>
            )}
          </div>
          <button onClick={save} disabled={saving} data-testid="shift-save"
            className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2 transition-colors">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Speichert …</> : "Nachweis anlegen"}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Nachweise</p>
        {loading && <p className="text-sm text-[#757067]">Lade …</p>}
        {!loading && list.length === 0 && (
          <p className="text-sm text-[#757067] italic py-8 text-center border border-dashed border-[#E3DECE]">
            Noch keine Stundennachweise.
          </p>
        )}
        {list.map((r) => (
          <div key={r.id} data-testid={`shift-${r.id}`} className="border border-[#E3DECE] bg-white p-4 md:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <span className="text-[10px] font-mono text-[#BA6D52]">{r.nachweis_number}</span>
                  {statusBadge(r)}
                </div>
                <p className="font-serif text-lg text-[#2C2A26]">{r.betrieb}</p>
                <p className="text-xs text-[#757067] mt-1">
                  {formatDateDE(r.datum)} · {r.von}–{r.bis} Uhr · <b>{num(r.hours)} Std.</b>
                  {r.stundensatz_eur > 0 && <> · {num(r.total_net_eur)} € netto</>}
                </p>
                {r.confirmed_at && (
                  <p className="text-xs text-emerald-700 mt-1">
                    Bestätigt von {r.confirmed_by} am {new Date(r.confirmed_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                )}
                {r.notiz && <p className="text-xs text-[#757067] italic mt-1">{r.notiz}</p>}
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                {!r.confirmed_at && (
                  <button onClick={() => sendWhatsApp(r)} data-testid={`shift-${r.id}-whatsapp`}
                    className="inline-flex items-center gap-2 bg-[#25D366] text-white hover:brightness-95 px-4 py-2 text-[10px] uppercase tracking-[0.15em] transition">
                    <MessageCircle size={13} /> WhatsApp
                  </button>
                )}
                {!r.confirmed_at && (
                  <button onClick={() => sendEmail(r)} data-testid={`shift-${r.id}-email`}
                    className="inline-flex items-center gap-2 border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em] transition">
                    <Mail size={13} /> E-Mail
                  </button>
                )}
                {!r.confirmed_at && (
                  <button onClick={() => copyLink(r)} data-testid={`shift-${r.id}-copy`}
                    className="inline-flex items-center gap-2 border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em] transition">
                    <Link2 size={13} /> Link
                  </button>
                )}
                <button onClick={() => openPdf(r)} data-testid={`shift-${r.id}-pdf`}
                  className="inline-flex items-center gap-2 border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em] transition">
                  <FileText size={13} /> PDF
                </button>
                {r.confirmed_at && (
                  <button onClick={() => toInvoice(r)} data-testid={`shift-${r.id}-invoice`}
                    className="inline-flex items-center gap-2 bg-[#BA6D52] text-white hover:bg-[#9E5841] px-4 py-2 text-[10px] uppercase tracking-[0.15em] transition">
                    <Receipt size={13} /> Rechnung
                  </button>
                )}
                <button onClick={() => remove(r)} data-testid={`shift-${r.id}-delete`}
                  className="inline-flex items-center gap-2 border border-red-200 text-red-700 hover:bg-red-50 px-3 py-2 text-[10px] uppercase tracking-[0.15em] transition">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
